// Mobile PWA install banner. Two paths:
//   1. Android/Chrome: shows once beforeinstallprompt has fired.
//   2. iOS Safari: shows a short instruction panel (tap Share →
//      Add to Home Screen), since iOS has no programmatic prompt.
//
// Both are dismissible + one-shot per user (localStorage flag) so we don't
// nag. Users can still trigger the install from the "My" dropdown at any
// time — see TopbarMe.

import { useEffect, useState } from 'react';
import { useInstall } from '../lib/pwa';

const DISMISS_KEY = 'onhere_install_dismissed';
const IOS_HINT_KEY = 'onhere_install_ios_hint_v1';

export function InstallPrompt() {
    const { canPrompt, standalone, ios, install } = useInstall();
    const [showIOSHint, setShowIOSHint] = useState(false);
    const [dismissed, setDismissed] = useState(
        typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1',
    );

    useEffect(() => {
        if (dismissed || standalone) return;
        if (typeof window === 'undefined' || window.innerWidth > 900) return;
        if (!ios) return;
        const t = setTimeout(() => {
            if (localStorage.getItem(IOS_HINT_KEY) !== '1') {
                setShowIOSHint(true);
                localStorage.setItem(IOS_HINT_KEY, '1');
            }
        }, 3000);
        return () => clearTimeout(t);
    }, [dismissed, standalone, ios]);

    if (dismissed || standalone) return null;
    if (typeof window !== 'undefined' && window.innerWidth > 900) return null;
    if (!canPrompt && !showIOSHint) return null;

    function dismiss() {
        localStorage.setItem(DISMISS_KEY, '1');
        setDismissed(true);
        setShowIOSHint(false);
    }

    return (
        <div style={bannerStyle} role="dialog" aria-label="Install OnHere">
            <div style={rowStyle}>
                <img src="/icon-192.png" alt="" width={40} height={40} style={{ border: '1px solid #111' }} />
                <div style={{ flex: 1 }}>
                    <div style={titleStyle}>Add OnHere to your Home Screen</div>
                    <div style={hintStyle}>
                        {canPrompt
                            ? 'Full-screen paper, offline reading, push notifications when a neighbour posts.'
                            : 'Tap the Share button, then "Add to Home Screen".'}
                    </div>
                </div>
                {canPrompt && (
                    <button type="button" onClick={install} style={installBtnStyle}>
                        Install
                    </button>
                )}
                <button type="button" onClick={dismiss} style={closeBtnStyle} aria-label="Dismiss">×</button>
            </div>
        </div>
    );
}

const bannerStyle: React.CSSProperties = {
    position: 'fixed',
    left: 8, right: 8, bottom: 8,
    padding: 12,
    background: '#fff',
    border: '1px solid #111',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 9999,
    borderRadius: 4,
    fontFamily: "'Newsreader', Georgia, serif",
};
const rowStyle: React.CSSProperties = {
    display: 'flex', gap: 12, alignItems: 'center',
};
const titleStyle: React.CSSProperties = {
    fontSize: 15, fontWeight: 600, color: '#111',
};
const hintStyle: React.CSSProperties = {
    fontSize: 12, color: '#666', marginTop: 2, fontFamily: '-apple-system, sans-serif',
};
const installBtnStyle: React.CSSProperties = {
    padding: '8px 14px', background: 'var(--accent)', color: '#fff',
    border: 'none', fontSize: 13, fontFamily: '-apple-system, sans-serif',
    textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer',
};
const closeBtnStyle: React.CSSProperties = {
    background: 'none', border: 'none', color: '#666',
    fontSize: 24, cursor: 'pointer', padding: '0 4px',
};
